import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaUsers, FaSearch } from 'react-icons/fa';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import hotelService from '../../services/hotelService';

const { RangePicker } = DatePicker;

const HotelSearchBar = ({ searchCriteria, onSearchUpdate, hotelData }) => {
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [tempSearchCriteria, setTempSearchCriteria] = useState(searchCriteria);
  const guestPickerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [usePortal, setUsePortal] = useState(false);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const dropdownContentRef = useRef(null);

  // Handle date range selection
  const handleDateRangeSelect = (dates, dateStrings) => {
    if (!dates || dates.length !== 2) return;
    
    // Ensure we get the full date format (YYYY-MM-DD) from dayjs objects
    const [checkInDate, checkOutDate] = dates.map(date => date.format('YYYY-MM-DD'));
    
    setTempSearchCriteria(prev => ({
      ...prev,
      checkInDate: checkInDate,
      checkOutDate: checkOutDate
    }));
  };

  // Handle guest count changes
  const handleGuestChange = (type, operation) => {
    setTempSearchCriteria(prev => {
      const newCriteria = { ...prev };
      
      if (type === 'adults') {
        newCriteria.adults = operation === 'increment' 
          ? Math.min(prev.adults + 1, 10)
          : Math.max(prev.adults - 1, 1);
      } else if (type === 'children') {
        newCriteria.children = operation === 'increment'
          ? Math.min(prev.children + 1, 10)
          : Math.max(prev.children - 1, 0);
      }
      
      return newCriteria;
    });
  };

  // Apply search updates
  const handleSearchUpdate = () => {
    const validation = hotelService.validateSearchCriteria(tempSearchCriteria);
    
    if (!validation.isValid) {
      alert(validation.errors.join('\n'));
      return;
    }
    
    onSearchUpdate(tempSearchCriteria);
    setShowGuestPicker(false);
  };

  // Reset to original criteria
  const handleReset = () => {
    setTempSearchCriteria(searchCriteria);
    setShowGuestPicker(false);
  };

  // Check if criteria has changed
  const hasChanges = JSON.stringify(tempSearchCriteria) !== JSON.stringify(searchCriteria);


  // Guest picker dropdown content
  const GuestPickerContent = () => (
    <div className="space-y-5" ref={dropdownContentRef}>
                  {/* Adults */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-light text-slate-800 text-base">Adults</div>
                      <div className="text-sm text-slate-500 font-light">Ages 18+</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
            onClick={(e) => {
              e.stopPropagation();
              handleGuestChange('adults', 'decrement');
            }}
                        disabled={tempSearchCriteria.adults <= 1}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 text-slate-600 font-light"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-light text-slate-800">{tempSearchCriteria.adults}</span>
                      <button
            onClick={(e) => {
              e.stopPropagation();
              handleGuestChange('adults', 'increment');
            }}
                        disabled={tempSearchCriteria.adults >= 10}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 text-slate-600 font-light"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-light text-slate-800 text-base">Children</div>
                      <div className="text-sm text-slate-500 font-light">Ages 0-17</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
            onClick={(e) => {
              e.stopPropagation();
              handleGuestChange('children', 'decrement');
            }}
                        disabled={tempSearchCriteria.children <= 0}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 text-slate-600 font-light"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-light text-slate-800">{tempSearchCriteria.children}</span>
                      <button
            onClick={(e) => {
              e.stopPropagation();
              handleGuestChange('children', 'increment');
            }}
                        disabled={tempSearchCriteria.children >= 10}
                        className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 text-slate-600 font-light"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
  );

  // Handle click outside to close guest picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the guest picker container
      if (guestPickerRef.current && guestPickerRef.current.contains(event.target)) {
        return; // Don't close if clicking inside
      }
      
      // Check if click is inside the dropdown content (both normal and portal)
      if (dropdownContentRef.current && dropdownContentRef.current.contains(event.target)) {
        return; // Don't close if clicking inside dropdown content
      }
      
      // Close the dropdown if clicking outside
      setShowGuestPicker(false);
    };

    if (showGuestPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showGuestPicker, usePortal]);

  return (
    <div className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 py-4 sm:py-6 relative shadow-sm min-w-0">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 lg:gap-6 lg:space-y-0 min-w-0">
        {/* Hotel Info */}
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-slate-800 mb-2 typography-section-title leading-tight tracking-wide line-clamp-2 sm:truncate sm:line-clamp-none">
            {hotelData?.hotelInfo?.hotelName || 'Hotel Details'}
          </h2>
          {hotelData?.hotelInfo?.location?.address && (
            <p className="text-sm sm:text-base text-slate-600 font-light break-words line-clamp-2">
              {hotelData.hotelInfo.location.address.city && hotelData.hotelInfo.location.address.city}
              {hotelData.hotelInfo.location.address.countryName && `, ${hotelData.hotelInfo.location.address.countryName}`}
            </p>
          )}
        </div>

        {/* Search Controls */}
        <div className="flex flex-col w-full lg:w-auto lg:min-w-0 lg:max-w-none gap-3 sm:flex-row sm:flex-wrap sm:items-stretch sm:gap-3">
          {/* Date Range Picker */}
          <div className="relative w-full sm:flex-1 sm:min-w-[240px] min-w-0">
            <RangePicker
              value={[
                dayjs(tempSearchCriteria.checkInDate),
                dayjs(tempSearchCriteria.checkOutDate)
              ]}
              onChange={handleDateRangeSelect}
              placeholder={['Check-in', 'Check-out']}
              format="MMM DD"
              className="w-full h-12 text-base font-light [&_.ant-picker-input]:text-sm sm:[&_.ant-picker-input]:text-base"
              size="large"
              disabledDate={(current) => {
                // Only disable dates before today (not including today)
                const today = dayjs().startOf('day');
                return current && current.isBefore(today);
              }}
              separator={<span className="text-slate-400 font-light">to</span>}
              style={{
                fontFamily: '"Poppins-Light", "Poppins", sans-serif',
                width: '100%',
                maxWidth: '100%'
              }}
            />
          </div>

          {/* Guests */}
          <div className="relative w-full sm:w-auto sm:flex-shrink-0" ref={guestPickerRef}>
            <button
              onClick={() => {
                // Calculate dropdown position based on viewport
                if (guestPickerRef.current) {
                  const rect = guestPickerRef.current.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;
                  const viewportWidth = window.innerWidth;
                  const dropdownWidth = 256; // w-64 = 16rem = 256px
                  
                  const spaceBelow = viewportHeight - rect.bottom;
                  const spaceAbove = rect.top;
                  const spaceRight = viewportWidth - rect.left;
                  const spaceLeft = rect.left;
                  
                  // Check if dropdown would be cut off
                  const wouldBeCutOff = spaceRight < dropdownWidth || spaceBelow < 200;
                  
                  if (wouldBeCutOff) {
                    // Use portal with fixed positioning
                    setUsePortal(true);
                    setPortalPosition({
                      top: rect.bottom + 8,
                      left: Math.max(8, Math.min(rect.left, viewportWidth - dropdownWidth - 8))
                    });
                  } else {
                    // Use normal positioning
                    setUsePortal(false);
                    
                    // Determine vertical position
                    let verticalPos = 'bottom';
                    if (spaceBelow < 200 && spaceAbove > 200) {
                      verticalPos = 'top';
                    }
                    
                    // Determine horizontal position
                    let horizontalPos = 'left';
                    if (spaceRight < dropdownWidth && spaceLeft > dropdownWidth) {
                      horizontalPos = 'right';
                    }
                    
                    setDropdownPosition(`${verticalPos}-${horizontalPos}`);
                  }
                }
                
                setShowGuestPicker(!showGuestPicker);
              }}
              className="flex items-center space-x-3 px-4 py-3 h-12 border border-slate-300 rounded-lg hover:border-slate-400 hover:shadow-sm transition-all duration-200 bg-white w-full sm:w-auto justify-between sm:justify-start"
            >
              <FaUsers className="text-slate-600 w-4 h-4" />
              <div className="text-left">
                <div className="text-xs text-slate-500 font-light">Guests</div>
                <div className="text-sm font-light text-slate-800">
                  {tempSearchCriteria.adults} adult{tempSearchCriteria.adults !== 1 ? 's' : ''}
                  {tempSearchCriteria.children > 0 && `, ${tempSearchCriteria.children} child${tempSearchCriteria.children !== 1 ? 'ren' : ''}`}
                </div>
              </div>
            </button>

            {/* Guest Picker Dropdown */}
            {showGuestPicker && !usePortal && (
              <div className={`absolute bg-white border border-slate-200 rounded-lg shadow-xl p-5 w-64 z-[9999] min-w-max max-w-[90vw] ${
                dropdownPosition.includes('top') 
                  ? 'bottom-full mb-2' 
                  : 'top-full mt-2'
              } ${
                dropdownPosition.includes('right')
                  ? 'right-0'
                  : 'left-0'
              }`}>
                <GuestPickerContent />
              </div>
            )}
          </div>

          {/* Nights Display */}
          <div className="flex items-center justify-center px-4 py-3 h-12 bg-slate-50 border border-slate-200 rounded-lg w-full sm:w-auto">
            <div className="text-center">
              <div className="text-xs text-slate-500 font-light">Nights</div>
              <div className="text-sm font-light text-slate-800">
                {hotelService.calculateNights(tempSearchCriteria.checkInDate, tempSearchCriteria.checkOutDate)}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {hasChanges && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleReset}
                className="px-5 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 font-light text-sm w-full sm:w-auto"
              >
                Reset
              </button>
              <button
                onClick={handleSearchUpdate}
                className="flex items-center justify-center space-x-2 px-5 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 hover:shadow-md transition-all duration-200 font-light text-sm w-full sm:w-auto"
              >
                <FaSearch className="w-4 h-4" />
                <span>Update Search</span>
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Portal-based Guest Picker Dropdown */}
      {showGuestPicker && usePortal && createPortal(
        <div 
          className="fixed bg-white border border-slate-200 rounded-lg shadow-xl p-5 w-64 z-[9999] min-w-max max-w-[90vw]"
          style={{
            top: `${portalPosition.top}px`,
            left: `${portalPosition.left}px`
          }}
          data-guest-picker-portal
        >
          <GuestPickerContent />
        </div>,
        document.body
      )}
    </div>
  );
};

export default HotelSearchBar;
